import {
    type BufferAttribute,
    BufferGeometry,
    type InterleavedBufferAttribute,
    Line3,
    type Plane,
    Vector3,
} from 'three';
import { vertexIndexBuffer, vertexPositionBuffer } from '@/domain/geometry/mesh';

const _FRONT_SIDE = 1 as const;
const _BACK_SIDE = 2 as const;

/**
 * Compute the points where a mesh's triangle edges intersect a plane.
 *
 * Returns segment-endpoint pairs: every two consecutive points (i, i+1) form a line segment
 * on the plane. This means the result can be used directly for rendering cross-section outlines
 * or flattened for nearest-point searches.
 *
 * Both the geometry and the plane must be in the same coordinate space.
 */
export function meshPlaneIntersectionPoints(geometry: BufferGeometry, plane: Plane): Vector3[] {
    if (!geometry.index) {
        return [];
    }

    const position = vertexPositionBuffer(geometry);
    const index = vertexIndexBuffer(geometry);
    const vertexSide = _calculateVertexSide(position, plane);

    const points: Vector3[] = [];

    const vA = new Vector3();
    const vP = new Vector3();
    const vQ = new Vector3();
    const lineAP = new Line3();
    const lineAQ = new Line3();
    const intersectionAP = new Vector3();
    const intersectionAQ = new Vector3();

    function addSegment(a: number, p: number, q: number) {
        vA.fromBufferAttribute(position, a);
        vP.fromBufferAttribute(position, p);
        vQ.fromBufferAttribute(position, q);
        lineAP.set(vA, vP);
        lineAQ.set(vA, vQ);
        const hitAP = plane.intersectLine(lineAP, intersectionAP);
        const hitAQ = plane.intersectLine(lineAQ, intersectionAQ);

        if (hitAP && hitAQ) {
            points.push(intersectionAP.clone(), intersectionAQ.clone());
        }
    }

    const faceCount = index.count / 3;
    for (let faceIndex = 0; faceIndex < faceCount; ++faceIndex) {
        const i0 = faceIndex * 3;
        const v0 = index.array[i0];
        const v1 = index.array[i0 + 1];
        const v2 = index.array[i0 + 2];

        const s0 = vertexSide[v0];
        const s1 = vertexSide[v1];
        const s2 = vertexSide[v2];

        if (s0 === _FRONT_SIDE) {
            if (s1 === _FRONT_SIDE) {
                if (s2 !== _FRONT_SIDE) addSegment(v2, v0, v1);
            } else {
                if (s2 === _FRONT_SIDE) addSegment(v1, v0, v2);
                else addSegment(v0, v1, v2);
            }
        } else {
            if (s1 === _FRONT_SIDE) {
                if (s2 === _FRONT_SIDE) addSegment(v0, v1, v2);
                else addSegment(v1, v0, v2);
            } else {
                if (s2 === _FRONT_SIDE) addSegment(v2, v0, v1);
            }
        }
    }

    return points;
}

type _PositionBuffer = BufferAttribute | InterleavedBufferAttribute;

function _calculateVertexSide(
    meshVertexPosition: _PositionBuffer,
    plane: Plane,
): Int8Array {
    const vertexCount = meshVertexPosition.count;
    const result = new Int8Array(vertexCount);
    const position = new Vector3();
    for (let vertexIndex = 0; vertexIndex < vertexCount; ++vertexIndex) {
        position.fromBufferAttribute(meshVertexPosition, vertexIndex);
        result[vertexIndex] = plane.distanceToPoint(position) >= 0 ? _FRONT_SIDE : _BACK_SIDE;
    }
    return result;
}